import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { TrendingUp, AlertCircle, SearchCheck, RefreshCw } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { useAcquisitionAnalysis } from '../hooks/useAcquisitionAnalysis';
import { useAnalysisHistory } from '../hooks/useAnalysisHistory';
import { AnalysisUploader } from '../components/acquisitionAnalysis/AnalysisUploader';
import { SupportedFormatsCard } from '../components/acquisitionAnalysis/SupportedFormatsCard';
import type { AcquisitionSourceFormat } from '../types/acquisitionAnalysis';
import { AnalysisProgress } from '../components/acquisitionAnalysis/AnalysisProgress';
import { AnalysisResultView } from '../components/acquisitionAnalysis/AnalysisResultView';
import { AnalysisHistory } from '../components/acquisitionAnalysis/AnalysisHistory';
import type { AppContextType } from '../components/layout/AppLayout';

// Helper to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Helper to determine file type
const getFileType = (file: File): 'excel' | 'csv' | 'pdf' => {
  const ext = file.name.toLowerCase().split('.').pop();
  if (ext === 'xlsx' || ext === 'xls') return 'excel';
  if (ext === 'csv') return 'csv';
  if (ext === 'pdf') return 'pdf';
  return 'excel'; // default
};

const AcquisitionAnalysisPage: React.FC = () => {
  const { userData } = useOutletContext<AppContextType>();
  const companyId = userData?.companyId;
  const [activeAnalysisId, setActiveAnalysisId] = useState<string | null>(null);
  // Use ref for interval to avoid re-render loops in useEffect
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const {
    initiateAnalysis,
    pollStatus,
    reset: resetAnalysis,
    status,
    result,
    isLoading,
    error,
    progress,
    currentStep,
    estimatedTimeRemaining,
  } = useAcquisitionAnalysis();

  const {
    analyses,
    isLoading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
    deleteAnalysis,
  } = useAnalysisHistory(companyId);

  // Poll for status updates when analysis is in progress
  useEffect(() => {
    // Clear any existing interval first
    if (pollIntervalRef.current) {
      console.log('[AcquisitionAnalysisPage] Clearing existing interval');
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (activeAnalysisId && companyId && status === 'processing') {
      console.log('[AcquisitionAnalysisPage] Starting polling for', activeAnalysisId);

      // Poll immediately on start
      pollStatus(activeAnalysisId, companyId);

      // Then poll every 3 seconds
      pollIntervalRef.current = setInterval(() => {
        console.log('[AcquisitionAnalysisPage] Polling tick...');
        pollStatus(activeAnalysisId, companyId);
      }, 3000);
    } else {
      console.log('[AcquisitionAnalysisPage] Not polling - activeAnalysisId:', activeAnalysisId, 'companyId:', companyId, 'status:', status);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollIntervalRef.current) {
        console.log('[AcquisitionAnalysisPage] Cleanup: clearing interval');
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [activeAnalysisId, companyId, status, pollStatus]);

  // Refresh history when analysis completes
  useEffect(() => {
    if (status === 'completed') {
      refetchHistory();
    }
  }, [status, refetchHistory]);

  const handleStartAnalysis = async (
    targetName: string,
    files: File[],
    fiscalYearStart: number,
    sourceFormat: AcquisitionSourceFormat
  ) => {
    if (!companyId) {
      console.error('No companyId available');
      return;
    }

    try {
      // Convert files to base64 format expected by backend
      const convertedFiles = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          base64: await fileToBase64(file),
          type: getFileType(file),
        }))
      );

      const analysisId = await initiateAnalysis({
        companyId,
        targetName,
        files: convertedFiles,
        options: {
          fiscalYearStart,
          sourceFormat,
        },
      });

      setActiveAnalysisId(analysisId);
    } catch (err) {
      console.error('Failed to start analysis:', err);
    }
  };

  const handleViewAnalysis = (analysisId: string) => {
    setActiveAnalysisId(analysisId);
    if (companyId) {
      pollStatus(analysisId, companyId);
    }
  };

  const handleDeleteAnalysis = (analysisId: string) => {
    setAnalysisToDelete(analysisId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!analysisToDelete) return;

    setDeleting(true);
    try {
      await deleteAnalysis(analysisToDelete);
      setShowDeleteConfirm(false);
      setAnalysisToDelete(null);
    } catch (err) {
      console.error('Failed to delete analysis:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleNewAnalysis = () => {
    setActiveAnalysisId(null);
    resetAnalysis();
  };

  // Determine which view to show
  const showErrorView = status === 'failed';
  const showUploadView = !status || status === 'pending';
  const showProgressView = status === 'processing' && !showErrorView;
  const showResultsView = status === 'completed' && result;

  return (
    <div className="space-y-6">
      <div>
        <PageHeader
          icon={SearchCheck}
          title="Trail Book Acquisition Analysis"
          subtitle="Enterprise-grade valuation and risk assessment powered by AI"
          actions={
            showResultsView ? (
              <button
                onClick={handleNewAnalysis}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-150"
              >
                New Analysis
              </button>
            ) : undefined
          }
        />

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-300">Analysis Error</h3>
                <p className="mt-1 text-sm text-red-400">{error.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area - Single column layout */}
        <div className="space-y-8">
          {/* Upload/Progress/Results Section */}
          <div>
            {showUploadView && (
              <>
                <SupportedFormatsCard />
                <AnalysisUploader
                  onStartAnalysis={handleStartAnalysis}
                  isLoading={isLoading}
                />
              </>
            )}

            {showProgressView && (
              <AnalysisProgress
                progress={progress}
                currentStep={currentStep}
                estimatedTimeRemaining={estimatedTimeRemaining}
              />
            )}

            {showErrorView && (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-red-500/20 rounded-xl">
                    <AlertCircle className="h-8 w-8 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-red-300 mb-2">
                      Analysis Failed
                    </h3>
                    <p className="text-slate-300 mb-4">
                      {error?.message || 'An unexpected error occurred while processing your analysis.'}
                    </p>
                    <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
                      <p className="text-sm text-slate-400">
                        <strong className="text-slate-300">Troubleshooting tips:</strong>
                      </p>
                      <ul className="text-sm text-slate-400 mt-2 space-y-1 list-disc list-inside">
                        <li>Ensure your Excel file has a "Broker" or "Name" column</li>
                        <li>Date columns should be formatted as months (e.g., "Jul 2023", "Jul-23")</li>
                        <li>Try re-exporting the file from your aggregator</li>
                      </ul>
                    </div>
                    <button
                      onClick={handleNewAnalysis}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showResultsView && result && (
              <AnalysisResultView result={result} companyId={companyId!} />
            )}
          </div>

          {/* Recent Analyses Section - Now below results */}
          <AnalysisHistory
            analyses={analyses}
            isLoading={historyLoading}
            error={historyError}
            onViewAnalysis={handleViewAnalysis}
            onDeleteAnalysis={handleDeleteAnalysis}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal - Valutten styled */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
            <p className="text-gray-300">
              Are you sure you want to delete this analysis? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setAnalysisToDelete(null);
                }}
                className="px-4 py-2 bg-gray-600 rounded-md hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcquisitionAnalysisPage;
